import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import {
    AlertTriangle, ArrowLeft, ArrowRight, Ban, Camera, Check, Clock, Expand, Loader2, Lock,
    MessageSquare, Plus, Send, Trash2, Truck,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import AccessRestricted from '../../components/AccessRestricted';
import { ImageLightbox } from '../../components/ui/image-lightbox';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { Vendor } from '../../types/lists.types';
import { Product, TransferOrderDetail, TransferStatus } from '../../types/stock.types';
import { vendorsApi } from '../../utils/lists-api';
import { productsApi, transfersApi } from '../../utils/stock-api';
import { ApiError } from '../../utils/api';
import { cn } from '../../components/ui/utils';
import { TransferStatusPill } from './TransferOrdersPage';
import SignaturePad from './components/SignaturePad';

/**
 * Transfer Order detail — STOCK.md §7.
 *
 * The screen is split by WHO OWNS WHAT. Everything under Departure belongs to the sending
 * yard and freezes the moment the truck leaves (§7.2). Everything under Arrival belongs to
 * the receiving yard and only unlocks while In Transit.
 *
 * The receiver cannot edit the manifest — deliberately. If they counted something
 * different they raise a discrepancy, which is recorded against the transfer without
 * changing what the sender declared. A manifest that can be edited after dispatch proves
 * nothing about what left.
 */
export default function TransferOrderDetailPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.STOCK_TRANSFER_ORDER);
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [order, setOrder] = useState<TransferOrderDetail | null>(null);
    const [products, setProducts] = useState<Product[]>([]);
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState<string | null>(null);

    const [productId, setProductId] = useState('');
    const [quantity, setQuantity] = useState('');

    const [signing, setSigning] = useState<'departure' | 'arrival' | null>(null);
    /** Server-side refusal (yard scoping) — rendered as a page, not eternal skeletons (UAT B9). */
    const [accessDenied, setAccessDenied] = useState<string | null>(null);
    const [signatureData, setSignatureData] = useState<string | null>(null);
    const [typedName, setTypedName] = useState('');

    const [voidOpen, setVoidOpen] = useState(false);
    const [voidReason, setVoidReason] = useState('');
    const [enterOpen, setEnterOpen] = useState(false);
    const [externalRef, setExternalRef] = useState('');
    const [discrepancyOpen, setDiscrepancyOpen] = useState(false);
    const [discrepancyNote, setDiscrepancyNote] = useState('');
    const [comment, setComment] = useState('');
    /**
     * Departure and arrival are two separate sets, so the viewer has to remember which
     * one it opened — an index alone would be ambiguous between them.
     */
    const [viewer, setViewer] = useState<{ stage: 'departure' | 'arrival'; index: number } | null>(null);

    const load = useCallback(async () => {
        if (!id) return;
        try {
            setLoading(true);
            setOrder(await transfersApi.get(Number(id)));
            setAccessDenied(null);
        } catch (err) {
            // A 403 here is yard scoping, not a transient failure — show it on the page
            // (the server's own wording) instead of leaving skeletons up forever (UAT B9).
            if (err instanceof ApiError && err.status === 403) {
                setAccessDenied(err.data?.message || err.message);
                return;
            }
            toast.error(err instanceof Error ? err.message : 'Failed to load transfer order');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { if (canRead) load(); }, [canRead, load]);

    useEffect(() => {
        if (!canRead) return;
        productsApi.list({ status: 'active' }).then(setProducts).catch(() => undefined);
        vendorsApi.list({ status: 'active' }).then(setVendors).catch(() => undefined);
    }, [canRead]);

    const run = async (key: string, fn: () => Promise<any>, message?: string) => {
        try {
            setBusy(key);
            await fn();
            if (message) toast.success(message);
            await load();
        } catch (err: any) {
            toast.error(err?.data?.message || err?.message || 'Something went wrong');
        } finally {
            setBusy(null);
        }
    };

    if (!canRead) return <AccessRestricted />;

    if (accessDenied) {
        return <AccessRestricted accessMessage={accessDenied}
                                 backMessage="Back to Transfer Orders" navigateTo="/stock/transfers" />;
    }

    if (loading || !order) {
        return (
            <div className="space-y-3 animate-pulse p-2 md:p-0">
                {[0, 1, 2].map((i) => <div key={i} className="h-24 bg-gray-300 rounded-md" />)}
            </div>
        );
    }

    const locked = order.originLocked;
    const canEditOrigin = !locked;
    const canAddArrival = order.status === 'in_transit' && order.isDestinationSide;
    const available = order.availableTransitions ?? [];

    const photosAt = (stage: 'departure' | 'arrival') =>
        order.photos.filter((p) => p.stage === stage);
    const signatureAt = (role: 'departure' | 'arrival') =>
        order.signatures.find((s) => s.role === role) ?? null;

    const transitionTo = (to: TransferStatus, extra: Record<string, any> = {}) =>
        run(`t:${to}`, () => transfersApi.transition(order.id, { to, ...extra }),
            `Moved to ${to.replace(/_/g, ' ')}`);

    const photoInput = (stage: 'departure' | 'arrival', disabled: boolean) => (
        <label className={cn(
            'inline-flex items-center gap-2 h-10 px-3 rounded-md border border-input cursor-pointer hover:bg-accent text-sm',
            disabled && 'opacity-50 pointer-events-none'
        )}>
            <Camera size={16} />Add photo
            <input
                type="file" accept="image/*" capture="environment" className="hidden"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) run(`photo:${stage}`,
                        () => transfersApi.uploadPhoto(order.id, stage, file), 'Photo added');
                }}
            />
        </label>
    );

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0 max-w-5xl">
            {/* Header */}
            <div>
                <Button variant="ghost" size="sm" onClick={() => navigate('/stock/transfers')} className="mb-2 -ml-2">
                    <ArrowLeft size={16} className="mr-1.5" />Transfer Orders
                </Button>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <h1 className="text-2xl font-bold font-mono">{order.orderNumber}</h1>
                            <TransferStatusPill status={order.status} />
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                            <span>
                                {order.originYard?.yardName}
                                {order.originYard?.branch?.name ? ` · ${order.originYard.branch.name}` : ''}
                            </span>
                            <ArrowRight size={14} />
                            <span>
                                {order.destinationYard?.yardName}
                                {order.destinationYard?.branch?.name ? ` · ${order.destinationYard.branch.name}` : ''}
                            </span>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {available.includes('packing') && (
                            <Button variant="outline" onClick={() => transitionTo('packing')} disabled={busy !== null}>
                                Start Packing
                            </Button>
                        )}
                        {available.includes('in_transit') && (
                            <Button onClick={() => transitionTo('in_transit')} disabled={busy !== null}>
                                <Truck size={16} className="mr-2" />Dispatch
                            </Button>
                        )}
                        {available.includes('received') && order.isDestinationSide && (
                            <Button onClick={() => transitionTo('received')} disabled={busy !== null}>
                                <Check size={16} className="mr-2" />Mark Received
                            </Button>
                        )}
                        {available.includes('ready_to_enter') && (
                            <Button onClick={() => transitionTo('ready_to_enter')} disabled={busy !== null}>
                                Ready to Enter
                            </Button>
                        )}
                        {available.includes('transfer_entered') && (
                            <Button onClick={() => { setExternalRef(''); setEnterOpen(true); }} disabled={busy !== null}>
                                <Send size={16} className="mr-2" />Mark Entered
                            </Button>
                        )}
                        {available.includes('voided') && (
                            <Button variant="outline" className="text-destructive"
                                    onClick={() => { setVoidReason(''); setVoidOpen(true); }}
                                    disabled={busy !== null}>
                                <Ban size={16} className="mr-2" />Void
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {order.status === 'in_transit' && (
                <Card className="border-amber-200 bg-amber-50/50">
                    <CardContent className="p-4 text-sm flex items-start gap-2">
                        <Clock size={16} className="text-amber-600 mt-0.5 shrink-0" />
                        <div>
                            <p className="font-medium text-amber-800">In transit</p>
                            <p className="text-amber-700 mt-0.5">
                                This material has left {order.originYard?.yardName} and has not arrived at{' '}
                                {order.destinationYard?.yardName}. It is on nobody's stock on hand until received.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            )}

            {order.status === 'voided' && order.voidReason && (
                <Card className="border-red-200 bg-red-50/50">
                    <CardContent className="p-4 text-sm">
                        <p className="font-medium text-red-800">Voided</p>
                        <p className="text-red-700 mt-1">{order.voidReason}</p>
                        <p className="text-xs text-red-600 mt-2">
                            The full manifest, photos and signatures are preserved in the audit trail.
                        </p>
                    </CardContent>
                </Card>
            )}

            {order.status === 'transfer_entered' && (
                <Card className="border-emerald-200 bg-emerald-50/50">
                    <CardContent className="p-4 text-sm">
                        <p className="font-medium text-emerald-800">Entered into the external stock system</p>
                        <p className="text-emerald-700 mt-1">
                            Reference <span className="font-mono font-semibold">{order.externalRef}</span>
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Material list */}
            <Card>
                <CardContent className="p-4 md:p-6">
                    <div className="flex items-center justify-between gap-3 mb-1">
                        <h2 className="font-semibold flex items-center gap-2">
                            {locked && <Lock size={16} className="text-muted-foreground" />}
                            Material list
                        </h2>
                        <span className="text-sm text-muted-foreground">{order.lines.length} line(s)</span>
                    </div>

                    {locked ? (
                        <p className="text-sm text-muted-foreground mb-4">
                            Locked — this is what the sending yard declared when the transfer left.
                            Nobody can change it, including the sender. If your count differs, raise a
                            discrepancy.
                        </p>
                    ) : (
                        <div className="grid grid-cols-[2fr_1fr_auto] gap-2 my-4">
                            <Select value={productId} onValueChange={setProductId}>
                                <SelectTrigger><SelectValue placeholder="Select a product" /></SelectTrigger>
                                <SelectContent>
                                    {products.map((p) => (
                                        <SelectItem key={p.id} value={String(p.id)}>
                                            {p.name} ({p.sku})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Input type="number" inputMode="numeric" min="1" placeholder="Qty"
                                   value={quantity} onChange={(e) => setQuantity(e.target.value)} />
                            <Button
                                disabled={busy !== null || !productId || !quantity}
                                onClick={() => run('addLine', async () => {
                                    await transfersApi.addLine(order.id, {
                                        productId: Number(productId), quantity: Number(quantity),
                                    });
                                    setProductId(''); setQuantity('');
                                })}
                            >
                                <Plus size={16} />
                            </Button>
                        </div>
                    )}

                    {order.lines.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4 text-center">Nothing on this transfer yet.</p>
                    ) : (
                        <div className="divide-y divide-border border border-border rounded-lg">
                            {order.lines.map((l) => (
                                <div key={l.id} className="flex items-center gap-3 p-3">
                                    <div className="min-w-0 flex-1">
                                        <p className="font-medium truncate">{l.product?.name}</p>
                                        <p className="text-xs text-muted-foreground font-mono">{l.product?.sku}</p>
                                    </div>
                                    <span className="font-medium tabular-nums w-16 text-right">{l.quantity}</span>
                                    <span className="text-xs text-muted-foreground w-12">{l.product?.uom}</span>
                                    {canEditOrigin && (
                                        <Button variant="ghost" size="icon" aria-label="Remove line"
                                                className="text-destructive" disabled={busy !== null}
                                                onClick={() => run(`rm:${l.id}`,
                                                    () => transfersApi.removeLine(order.id, l.id))}>
                                            <Trash2 size={16} />
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Departure — the sending yard's side */}
            <Card>
                <CardContent className="p-4 md:p-6">
                    <h2 className="font-semibold flex items-center gap-2 mb-1">
                        {locked && <Lock size={16} className="text-muted-foreground" />}
                        Departure — {order.originYard?.yardName}
                    </h2>
                    <p className="text-sm text-muted-foreground mb-4">
                        {locked
                            ? 'Frozen since dispatch.'
                            : 'All of this is required before the transfer can leave.'}
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <div>
                            <Label htmlFor="vendor">Transport Vendor</Label>
                            {locked ? (
                                <p className="text-sm mt-1">{order.vendor?.tradingName ?? '–'}</p>
                            ) : (
                                <Select
                                    value={order.vendor ? String(order.vendor.id) : ''}
                                    onValueChange={(v) => run('vendor',
                                        () => transfersApi.update(order.id, { vendorId: Number(v) }))}
                                >
                                    <SelectTrigger id="vendor"><SelectValue placeholder="Select a carrier" /></SelectTrigger>
                                    <SelectContent>
                                        {vendors.map((v) => (
                                            <SelectItem key={v.id} value={String(v.id)}>{v.tradingName}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>
                        <div>
                            <Label htmlFor="departed">Date Material Left</Label>
                            {locked ? (
                                <p className="text-sm mt-1">
                                    {order.departedAt ? String(order.departedAt).slice(0, 10) : '–'}
                                </p>
                            ) : (
                                <Input
                                    id="departed" type="date"
                                    defaultValue={order.departedAt ? String(order.departedAt).slice(0, 10) : ''}
                                    onChange={(e) => run('departed',
                                        () => transfersApi.update(order.id, { departedAt: e.target.value }))}
                                />
                            )}
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 mb-3">
                        <span className="text-sm text-muted-foreground">
                            {photosAt('departure').length} departure photo(s)
                        </span>
                        {!locked && photoInput('departure', busy !== null)}
                    </div>
                    {photosAt('departure').length > 0 && (
                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4">
                            {photosAt('departure').map((p, i) => (
                                <button key={p.id} type="button"
                                        onClick={() => setViewer({ stage: 'departure', index: i })}
                                        aria-label={`Open departure photo ${i + 1}`}
                                        className="group relative rounded-md overflow-hidden border border-border focus:outline-none focus:ring-2 focus:ring-ring">
                                    <img src={p.url} alt="Departure"
                                         className="aspect-square w-full object-cover transition-transform group-hover:scale-105" />
                                    <span className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                                        <Expand size={18}
                                                className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="border-t border-border pt-3">
                        <p className="text-sm font-medium mb-1">Departure signature</p>
                        {signatureAt('departure') ? (
                            <div className="flex items-center gap-3 text-sm">
                                <img src={signatureAt('departure')!.signatureUrl} alt="Signature"
                                     className="h-14 border border-border rounded bg-white" />
                                <div>
                                    <p className="font-medium">{signatureAt('departure')!.typedName}</p>
                                    <p className="text-xs text-muted-foreground">
                                        {signatureAt('departure')!.user?.name}
                                    </p>
                                </div>
                            </div>
                        ) : locked ? (
                            <p className="text-sm text-muted-foreground">Not signed.</p>
                        ) : (
                            <Button variant="outline" size="sm"
                                    onClick={() => { setSignatureData(null); setTypedName(''); setSigning('departure'); }}>
                                Sign
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Arrival — the receiving yard's side */}
            <Card className={cn(order.status === 'draft' || order.status === 'packing' ? 'opacity-60' : '')}>
                <CardContent className="p-4 md:p-6">
                    <h2 className="font-semibold mb-1">
                        Arrival — {order.destinationYard?.yardName}
                    </h2>
                    <p className="text-sm text-muted-foreground mb-4">
                        {order.status === 'draft' || order.status === 'packing'
                            ? 'Available once the transfer is dispatched.'
                            : order.isDestinationSide
                                ? 'The only three things the receiving yard adds. All are required to mark it Received.'
                                : 'Added by the receiving yard.'}
                    </p>

                    <div className="mb-4">
                        <Label htmlFor="arrived">Date Material Arrived</Label>
                        {canAddArrival ? (
                            <Input
                                id="arrived" type="date"
                                defaultValue={order.arrivedAt ? String(order.arrivedAt).slice(0, 10) : ''}
                                onChange={(e) => run('arrived',
                                    () => transfersApi.update(order.id, { arrivedAt: e.target.value }))}
                                className="max-w-xs"
                            />
                        ) : (
                            <p className="text-sm mt-1">
                                {order.arrivedAt ? String(order.arrivedAt).slice(0, 10) : '–'}
                            </p>
                        )}
                    </div>

                    <div className="flex flex-wrap items-center gap-3 mb-3">
                        <span className="text-sm text-muted-foreground">
                            {photosAt('arrival').length} arrival photo(s)
                        </span>
                        {canAddArrival && photoInput('arrival', busy !== null)}
                    </div>
                    {photosAt('arrival').length > 0 && (
                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4">
                            {photosAt('arrival').map((p, i) => (
                                <button key={p.id} type="button"
                                        onClick={() => setViewer({ stage: 'arrival', index: i })}
                                        aria-label={`Open arrival photo ${i + 1}`}
                                        className="group relative rounded-md overflow-hidden border border-border focus:outline-none focus:ring-2 focus:ring-ring">
                                    <img src={p.url} alt="Arrival"
                                         className="aspect-square w-full object-cover transition-transform group-hover:scale-105" />
                                    <span className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                                        <Expand size={18}
                                                className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="border-t border-border pt-3">
                        <p className="text-sm font-medium mb-1">Receiving signature</p>
                        {signatureAt('arrival') ? (
                            <div className="flex items-center gap-3 text-sm">
                                <img src={signatureAt('arrival')!.signatureUrl} alt="Signature"
                                     className="h-14 border border-border rounded bg-white" />
                                <div>
                                    <p className="font-medium">{signatureAt('arrival')!.typedName}</p>
                                    <p className="text-xs text-muted-foreground">
                                        {signatureAt('arrival')!.user?.name}
                                    </p>
                                </div>
                            </div>
                        ) : canAddArrival ? (
                            <Button variant="outline" size="sm"
                                    onClick={() => { setSignatureData(null); setTypedName(''); setSigning('arrival'); }}>
                                Sign
                            </Button>
                        ) : (
                            <p className="text-sm text-muted-foreground">Not signed.</p>
                        )}
                    </div>

                    {/* §16.1.6 — the alternative to editing someone else's manifest. */}
                    {order.isDestinationSide && ['in_transit', 'received'].includes(order.status) && (
                        <div className="border-t border-border mt-4 pt-4">
                            <p className="text-sm font-medium mb-1 flex items-center gap-1.5">
                                <AlertTriangle size={14} className="text-amber-600" />
                                Counted something different?
                            </p>
                            <p className="text-xs text-muted-foreground mb-2">
                                You can't edit the sending yard's list — raise a discrepancy instead and
                                a manager will resolve it. The manifest stays exactly as sent.
                            </p>
                            <Button variant="outline" size="sm"
                                    onClick={() => { setDiscrepancyNote(''); setDiscrepancyOpen(true); }}>
                                Raise discrepancy
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Comments */}
            <Card>
                <CardContent className="p-4 md:p-6">
                    <h2 className="font-semibold flex items-center gap-2 mb-3">
                        <MessageSquare size={18} />Comments
                    </h2>
                    <div className="flex flex-col sm:flex-row gap-2 mb-4">
                        <Textarea rows={2} placeholder="Add context…" value={comment}
                                  onChange={(e) => setComment(e.target.value)} />
                        <Button disabled={busy !== null || !comment.trim()}
                                onClick={() => run('comment', async () => {
                                    await transfersApi.comment(order.id, comment.trim());
                                    setComment('');
                                })}>
                            Post
                        </Button>
                    </div>
                    {order.comments.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No comments.</p>
                    ) : (
                        <div className="space-y-3">
                            {order.comments.map((c) => (
                                <div key={c.id} className="text-sm">
                                    <p className="font-medium">
                                        {c.user?.name}
                                        <span className="text-xs text-muted-foreground font-normal ml-2">
                                            {new Date(c.createdAt).toLocaleString()}
                                        </span>
                                    </p>
                                    <p className="text-muted-foreground whitespace-pre-wrap">{c.body}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Audit trail */}
            <Card>
                <CardContent className="p-4 md:p-6">
                    <h2 className="font-semibold mb-3">Audit trail</h2>
                    <div className="space-y-2 text-sm">
                        {order.history.map((h) => (
                            <div key={h.id} className="flex flex-wrap items-baseline gap-x-2">
                                <span className="text-muted-foreground text-xs tabular-nums">
                                    {new Date(h.createdAt).toLocaleString()}
                                </span>
                                <span>
                                    {h.fromStatus === h.toStatus
                                        ? 'note'
                                        : h.fromStatus
                                            ? `${String(h.fromStatus).replace(/_/g, ' ')} → ${String(h.toStatus).replace(/_/g, ' ')}`
                                            : `created as ${String(h.toStatus).replace(/_/g, ' ')}`}
                                </span>
                                <span className="text-muted-foreground">· {h.actorName}</span>
                                {h.reason && <span className="text-muted-foreground w-full">{h.reason}</span>}
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Sign dialog */}
            <Dialog open={Boolean(signing)} onOpenChange={(o) => { if (!o) setSigning(null); }}>
                <DialogContent className="sm:max-w-[520px]">
                    <DialogHeader>
                        <DialogTitle>
                            {signing === 'arrival' ? 'Receiving signature' : 'Departure signature'}
                        </DialogTitle>
                        <DialogDescription>
                            Your typed name is shown on the record; the signature is bound to your
                            logged-in account.
                        </DialogDescription>
                    </DialogHeader>
                    <SignaturePad onChange={setSignatureData} />
                    <Input placeholder="Type your full name" value={typedName}
                           onChange={(e) => setTypedName(e.target.value)} />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSigning(null)}>Cancel</Button>
                        <Button
                            disabled={busy !== null || !signatureData || !typedName.trim()}
                            onClick={async () => {
                                if (!signing || !signatureData) return;
                                await run('sign', () => transfersApi.sign(order.id, {
                                    role: signing, signatureUrl: signatureData, typedName: typedName.trim(),
                                }), 'Signed');
                                setSigning(null);
                            }}
                        >
                            Sign
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Discrepancy */}
            <Dialog open={discrepancyOpen} onOpenChange={setDiscrepancyOpen}>
                <DialogContent className="sm:max-w-[520px]">
                    <DialogHeader>
                        <DialogTitle>Raise a discrepancy</DialogTitle>
                        <DialogDescription>
                            Recorded against this transfer with a snapshot of the manifest as it stands.
                            It does not change the material list and does not move the transfer — a
                            manager resolves it.
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <Label htmlFor="disc">What differs? * (at least 20 characters)</Label>
                        <Textarea id="disc" rows={4} value={discrepancyNote}
                                  onChange={(e) => setDiscrepancyNote(e.target.value)}
                                  placeholder="e.g. Manifest says 40 standards, counted 37 on arrival." />
                        <p className="text-xs text-muted-foreground mt-1">{discrepancyNote.trim().length}/20</p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDiscrepancyOpen(false)}>Cancel</Button>
                        <Button
                            disabled={busy !== null || discrepancyNote.trim().length < 20}
                            onClick={async () => {
                                await run('disc', () => transfersApi.raiseDiscrepancy(order.id, discrepancyNote.trim()),
                                    'Discrepancy recorded');
                                setDiscrepancyOpen(false);
                            }}
                        >
                            Raise discrepancy
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Void */}
            <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
                <DialogContent className="sm:max-w-[520px]">
                    <DialogHeader>
                        <DialogTitle>Void {order.orderNumber}</DialogTitle>
                        <DialogDescription>
                            The manifest, photos, signatures and who dispatched are snapshotted into the
                            audit trail before anything changes — including if the truck never arrives.
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <Label htmlFor="void-reason">Reason * (at least 20 characters)</Label>
                        <Textarea id="void-reason" rows={3} value={voidReason}
                                  onChange={(e) => setVoidReason(e.target.value)} />
                        <p className="text-xs text-muted-foreground mt-1">{voidReason.trim().length}/20</p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setVoidOpen(false)}>Cancel</Button>
                        <Button variant="destructive"
                                disabled={busy !== null || voidReason.trim().length < 20}
                                onClick={async () => {
                                    await transitionTo('voided', { reason: voidReason.trim() });
                                    setVoidOpen(false);
                                }}>
                            <Ban size={16} className="mr-2" />Void Transfer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Mark Entered */}
            <Dialog open={enterOpen} onOpenChange={setEnterOpen}>
                <DialogContent className="sm:max-w-[480px]">
                    <DialogHeader>
                        <DialogTitle>Mark {order.orderNumber} Entered</DialogTitle>
                        <DialogDescription>
                            Confirms the movement has been keyed into the external stock system. This is
                            terminal and the reference cannot be edited afterwards.
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <Label htmlFor="ext-ref">External Reference Number *</Label>
                        <Input id="ext-ref" value={externalRef} maxLength={16} className="font-mono"
                               onChange={(e) => setExternalRef(e.target.value)} placeholder="e.g. TR-88213" />
                        <p className="text-xs text-muted-foreground mt-1">Max 16 characters.</p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEnterOpen(false)}>Cancel</Button>
                        <Button disabled={busy !== null || !externalRef.trim()}
                                onClick={async () => {
                                    await transitionTo('transfer_entered', { externalRef: externalRef.trim() });
                                    setEnterOpen(false);
                                }}>
                            <Send size={16} className="mr-2" />Mark Entered
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ImageLightbox
                alt={viewer?.stage === 'arrival' ? 'Arrival photo' : 'Departure photo'}
                images={(viewer ? photosAt(viewer.stage) : []).map((p) => ({
                    id: p.id,
                    url: p.url,
                    label: viewer?.stage === 'arrival' ? 'Arrival' : 'Departure',
                    // serverTimestamp, not deviceTimestamp — §12.1 records the device clock
                    // but does not trust it.
                    caption: [p.capturedBy?.name, new Date(p.serverTimestamp).toLocaleString()]
                        .filter(Boolean).join(' · '),
                }))}
                index={viewer?.index ?? null}
                onIndexChange={(i) => setViewer(
                    i === null || !viewer ? null : { ...viewer, index: i })}
            />
        </div>
    );
}
